import { useAnalytics } from '@inflearn/analytics';
import { useCallback, useMemo, useRef } from 'react';

import { NEXT_PUBLIC_MIXPANEL_TOKEN } from '../../../appConfig';
import useClientAccountProfileQuery from '../../queries/useClientAccountProfileQuery';
import type { EventNames, EventPropsMap } from './eventNames';

export const useTrackMixpanelEvent = () => {
  const { analytics } = useAnalytics();
  const { data: profile, isLoading } = useClientAccountProfileQuery();

  const trackedEvents = useRef<Set<string>>(new Set());

  const userProperty = useMemo(() => {
    if (!profile?.id) {
      return {
        user_id: null,
        user_name: null,
        user_email: null,
        role_b2b: null,
        role_b2c: null,
        role_instructor: null,
        role_admin: null,
      };
    }

    return {
      user_id: profile.id,
      user_name: profile.name,
      user_email: profile.email,
      role_b2b: profile.group !== null,
      role_b2c: profile.group === null,
      role_instructor: profile.isInstructor,
      role_admin: profile.isAdmin,
    };
  }, [profile]);

  const trackMixpanelEvent = useCallback(
    <K extends EventNames>(
      eventName: K,
      props?: EventPropsMap[K] extends undefined ? never : Partial<EventPropsMap[K]>,
      options?: {
        once?: boolean;
      },
    ) => {
      if (options?.once) {
        if (trackedEvents.current.has(eventName)) {
          return;
        }

        trackedEvents.current.add(eventName);
      }

      void analytics.track(eventName, {
        mixpanelToken: NEXT_PUBLIC_MIXPANEL_TOKEN,
        mxp___user: userProperty,
        ...props,
      });
    },
    [analytics, userProperty],
  );

  return { trackMixpanelEvent, isReady: !isLoading };
};
